/**
 * NOVAPAY-116-R5A — exact payment lifecycle guards.
 * Browser return alone never means paid; confirming only after a genuine return signal.
 */

export type NovapayUiPhase =
  | "preparing_handoff"
  | "awaiting_payment"
  | "returned_pending_confirmation"
  | "paid"
  | "failed"
  | "idle";

const RETURN_EXPECTED_KEY = "asiastyle.store-v2.novapay.return_expected.v1";

export type ReturnExpected = {
  orderId: string;
  at: number;
};

function readReturnExpected(): ReturnExpected | null {
  try {
    const raw = window.sessionStorage.getItem(RETURN_EXPECTED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReturnExpected;
    if (!parsed?.orderId || !parsed.at) return null;
    // 2h window
    if (Date.now() - parsed.at > 2 * 60 * 60 * 1000) {
      window.sessionStorage.removeItem(RETURN_EXPECTED_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Call only when hosted navigation is actually invoked with a real URL. */
export function markNovapayReturnExpected(orderId: string): void {
  try {
    window.sessionStorage.setItem(
      RETURN_EXPECTED_KEY,
      JSON.stringify({ orderId, at: Date.now() } satisfies ReturnExpected),
    );
  } catch {
    /* ignore */
  }
  try {
    void import("./payment-resume-v2.js").then(({ patchPaymentResumeFlowState }) => {
      patchPaymentResumeFlowState("provider_open");
    });
  } catch {
    /* ignore */
  }
}

export function clearNovapayReturnExpected(): void {
  try {
    window.sessionStorage.removeItem(RETURN_EXPECTED_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Confirming is allowed only when:
 * - URL has payment_return=success|fail (provider return), AND
 * - we previously marked an expected return for this order (hosted open happened).
 * Sticky payment_return from an old tab must NOT alone trigger confirming.
 */
export function resolveNovapayUiPhase(input: {
  paymentMethod: string | null | undefined;
  paymentReturn: "success" | "fail" | null;
  lifecycleStatus: string | null | undefined;
  orderId: string | null | undefined;
  preparingHandoff?: boolean;
}): NovapayUiPhase {
  if (input.paymentMethod !== "novapay_card") return "idle";
  const status = String(input.lifecycleStatus ?? "");
  if (status === "paid") {
    clearNovapayReturnExpected();
    try {
      void import("./payment-resume-v2.js").then(({ clearPaymentResume }) => clearPaymentResume());
    } catch {
      /* ignore */
    }
    return "paid";
  }
  if (status === "failed" || status === "cancelled" || status === "expired") {
    return "failed";
  }
  if (input.preparingHandoff) return "preparing_handoff";

  const expected = readReturnExpected();
  const orderMatch = Boolean(input.orderId && expected?.orderId === input.orderId);
  const genuineReturn =
    (input.paymentReturn === "success" || input.paymentReturn === "fail") && orderMatch;

  if (genuineReturn && status !== "paid") {
    return "returned_pending_confirmation";
  }
  return "awaiting_payment";
}

/** Strip stale payment_return from the address bar without reload when not genuine. */
export function scrubStalePaymentReturnQuery(orderId: string | null | undefined): void {
  try {
    const url = new URL(window.location.href);
    const pr = url.searchParams.get("payment_return");
    if (pr !== "success" && pr !== "fail") return;
    const expected = readReturnExpected();
    const genuine = Boolean(orderId && expected?.orderId === orderId);
    if (genuine) return;
    url.searchParams.delete("payment_return");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* ignore */
  }
}

/**
 * STAGING-LAUNCH-READINESS-002-R2-R4 — allow changing payment method while
 * NovaPay is hanging/awaiting (not only after hard fail). Still blocked once paid.
 */
export function canChangePaymentMethod(phase: NovapayUiPhase): boolean {
  return (
    phase === "failed" ||
    phase === "idle" ||
    phase === "awaiting_payment" ||
    phase === "preparing_handoff" ||
    phase === "returned_pending_confirmation"
  );
}
